import { Button, CircularProgress, Typography } from "@mui/material";
import { sortBy } from "lodash";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, useState } from "react";

import {
  CampaignEventType,
  CampaignInitialSendBeginEvent,
  CampaignInitialSendScheduleEvent,
  CancelCampaignInitialSendMutation,
  CancelCampaignInitialSendMutationVariables,
  DisableTexterSendMutation,
  DisableTexterSendMutationVariables,
} from "~frontend/__generated__/graphql";
import { LinkButton } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import {
  CANCEL_CAMPAIGN_INITIAL_SEND,
  DISABLE_TEXTER_SEND,
  GET_CAMPAIGN_FOR_VIEW,
} from "~frontend/graphql/campaigns";
import { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

import { CampaignForView } from "../types";
import RoiTestSummary from "./RoiTestSummary";
import { SendButtons } from "./SendButtons";

export default function InitialSendRunning({
  campaign,
  hasActBlueIntegration,
  revealSendStatus,
}: {
  campaign: CampaignForView;
  hasActBlueIntegration: boolean;
  revealSendStatus: () => void;
}): ReactElement {
  const confirm = useConfirm();
  const [canceling, setCanceling] = useState(false);

  const [cancelInitialSend] = useMutation<
    CancelCampaignInitialSendMutation,
    CancelCampaignInitialSendMutationVariables
  >(CANCEL_CAMPAIGN_INITIAL_SEND, {
    onErrorSnackbarMessage: "Failed to cancel initial send",
    variables: {
      input: {
        campaignId: campaign.id,
      },
    },
  });

  async function handleCancel() {
    if (canceling) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Cancel Send",
      description: "Are you sure you want to cancel the send?",
      confirmationText: "Cancel Send",
      cancellationText: "No",
    });
    if (!confirmed) {
      return;
    }

    setCanceling(true);
    await cancelInitialSend();
    setCanceling(false);
  }

  const [disableTexterSend] = useMutation<
    DisableTexterSendMutation,
    DisableTexterSendMutationVariables
  >(DISABLE_TEXTER_SEND, {
    onErrorSnackbarMessage: "Failed to disable texter send",
    variables: {
      input: {
        campaignId: campaign.id,
      },
    },
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
  });

  async function handleDisableTexterSend() {
    if (canceling) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Pause Campaign Sending",
      description: "Are you sure you want to pause campaign sending?",
      confirmationText: "Pause Sending",
      cancellationText: "No",
    });
    if (!confirmed) {
      return;
    }

    setCanceling(true);
    await disableTexterSend();
    setCanceling(false);
  }

  const partialSendEvents = sortBy(
    campaign.events.filter(ev =>
      [
        CampaignEventType.InitialSendBegin,
        CampaignEventType.InitialSendSchedule,
      ].includes(ev.type)
    ),
    ev => -1 * ev.createdAt
  ) as Array<CampaignInitialSendBeginEvent | CampaignInitialSendScheduleEvent>;

  const sendPortionSize = partialSendEvents.length
    ? partialSendEvents[0].data.partialSendQty || 0
    : 0;

  if (campaign.texterSendEnabled) {
    return (
      <>
        <SendButtons
          campaign={campaign}
          hasActBlueIntegration={hasActBlueIntegration}
          revealSendStatus={revealSendStatus}
        />
        <Typography variant="body2" sx={{ color: palette.darkGray }}>
          Texter Send is turned on. Texters are able to send batches of initial
          texts, {campaign.texterSendBatchSize!.toLocaleString()} at a time
          until all initials are sent. If you want to bulk send the remainder of
          the initials, you must first{" "}
          {canceling ? (
            <>
              <Loading size={10} style={{ display: "inline" }} /> pause campaign
              sending
            </>
          ) : (
            <LinkButton onClick={handleDisableTexterSend}>
              pause campaign sending
            </LinkButton>
          )}
          .
        </Typography>
      </>
    );
  }

  return (
    <div>
      <p>
        <span style={{ paddingRight: "5px" }}>
          <CircularProgress size="12px" thickness={4.5} />
        </span>

        {campaign.quickStats?.initialSentTarget == null ? (
          "Full send in progress."
        ) : (
          <>
            Partial send in progress (stopping after{" "}
            {sendPortionSize.toLocaleString()} texts).
          </>
        )}
      </p>

      <RoiTestSummary campaign={campaign} />

      <div>
        <Button variant="contained" disabled={canceling} onClick={handleCancel}>
          {canceling ? "Cancelling..." : "Cancel"}
        </Button>
      </div>
    </div>
  );
}
